import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = 'June 2025';

  return (
    <div className="min-h-screen pt-20 bg-white">
      {/* Hero */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-gray-500">Last updated: {lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 prose prose-gray max-w-none">

          <div className="space-y-10 text-gray-700 leading-relaxed">

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p>
                Spire Group Inc. (&ldquo;Spire Group&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) is committed to protecting the privacy and personal information of our clients, website visitors, and business contacts. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or engage our services.
              </p>
              <p className="mt-3">
                By using our website or services, you agree to the collection and use of information in accordance with this policy. If you do not agree, please do not use our website or services.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">2.1 Information You Provide Directly</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Name, email address, phone number when you fill out a contact form or request a consultation</li>
                <li>Details about your real estate needs, practice type, and geographic preferences</li>
                <li>Communications you send to us by email, phone, or through our website chatbot</li>
              </ul>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">2.2 Information Collected Automatically</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Browser type, IP address, and device information</li>
                <li>Pages visited, time spent on pages, and referring URLs</li>
                <li>Cookies and similar tracking technologies (see Section 6)</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Respond to your inquiries and provide real estate consulting services</li>
                <li>Send consultation confirmations, follow-ups, and relevant communications</li>
                <li>Match you with the appropriate consultant based on your location and needs</li>
                <li>Improve our website, services, and client experience</li>
                <li>Comply with applicable legal obligations</li>
                <li>Send periodic updates or newsletters (with your consent, which you may withdraw at any time)</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. How We Share Your Information</h2>
              <p>We do <strong>not</strong> sell, trade, or rent your personal information to third parties. We may share your information in limited circumstances:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li><strong>Within our team:</strong> Your information is shared with the Spire Group consultant best suited to assist you.</li>
                <li><strong>Service providers:</strong> We work with trusted third-party tools (email services, CRM software) who process data strictly on our behalf and are bound by confidentiality obligations.</li>
                <li><strong>Legal requirements:</strong> We may disclose your information if required by law, court order, or regulatory authority.</li>
                <li><strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred with appropriate notice.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Retention</h2>
              <p>
                We retain your personal information for as long as necessary to fulfill the purposes described in this policy, or as required by applicable law. Client records related to real estate transactions may be retained for up to seven (7) years in accordance with professional and legal obligations. You may request deletion of your information at any time (subject to legal retention requirements) by contacting us at <a href="mailto:info@spiregrp.com" className="text-spire-purple hover:underline">info@spiregrp.com</a>.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Cookies</h2>
              <p>
                Our website uses cookies and similar technologies to enhance your browsing experience. Cookies are small text files stored on your device. We use:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li><strong>Essential cookies:</strong> Required for the website to function properly.</li>
                <li><strong>Analytics cookies:</strong> Help us understand how visitors interact with our site (e.g., Google Analytics).</li>
              </ul>
              <p className="mt-3">You can control cookie settings through your browser. Disabling cookies may affect website functionality.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Security</h2>
              <p>
                We implement reasonable administrative, technical, and physical safeguards to protect your personal information from unauthorized access, disclosure, alteration, or destruction. However, no method of transmission over the Internet is completely secure, and we cannot guarantee absolute security.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Your Rights (PIPEDA / CASL)</h2>
              <p>As a resident of Canada, you have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Withdraw consent to the use of your personal information</li>
                <li>Request deletion of your personal information (subject to legal obligations)</li>
                <li>File a complaint with the Office of the Privacy Commissioner of Canada</li>
              </ul>
              <p className="mt-3">To exercise any of these rights, please contact us at <a href="mailto:info@spiregrp.com" className="text-spire-purple hover:underline">info@spiregrp.com</a>.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Third-Party Links</h2>
              <p>
                Our website may contain links to third-party websites (such as Calendly, LinkedIn). We are not responsible for the privacy practices of those sites and encourage you to review their privacy policies.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Changes will be posted on this page with a revised &ldquo;Last Updated&rdquo; date. We encourage you to review this policy periodically.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact Us</h2>
              <p>If you have questions or concerns about this Privacy Policy, please contact:</p>
              <div className="mt-4 p-6 bg-gray-50 rounded-2xl">
                <p className="font-bold text-gray-900">Spire Group Inc.</p>
                <p>Calgary, Alberta, Canada</p>
                <p>Email: <a href="mailto:info@spiregrp.com" className="text-spire-purple hover:underline">info@spiregrp.com</a></p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Back link */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 flex gap-6">
          <Link to="/" className="text-spire-purple hover:underline font-semibold">&larr; Back to Home</Link>
          <Link to="/terms" className="text-spire-purple hover:underline font-semibold">Terms of Use &rarr;</Link>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
